import React, { createContext, ReactNode, use, useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { DatadogActionCreator } from '@bdc-libs/matrix.web-tracking'
import { rum } from '@divvy-web/utils.log'

import { useAuth } from '@sputnik/auth'
import useGetQueryParamsForCreditInit from '@sputnik/pages/PreApplicationPages/useGetQueryParamsForCreditInit'

import { useGetDataForTracking } from './GetDataForTracking.gql'
import { createTypedTracker, HostTracker } from './webtracking'

const GITLAB_PROJECT_ID = 386
const writeKey = window.__DivvyEnvironment.SEGMENT_WRITE_KEY

type WebTrackingProviderValue = { tracker: HostTracker | undefined }

const WebTrackingContext = createContext<WebTrackingProviderValue>({ tracker: undefined })

export const useWebTracking = () => use<WebTrackingProviderValue>(WebTrackingContext)

export const WebTrackingProvider = ({ children }: { children: ReactNode }) => {
  const { appId } = useParams()
  const { email } = useAuth()

  const { data } = useGetDataForTracking({
    variables: { creditApplicationId: appId || '' },
    skip: !appId,
  })

  const { creditApplication } = data || {}

  const { bdcOrgId, companyDivvyUuid, authorizedSigner, businessInfo, salesforceCreditId } = creditApplication || {}

  const { applicantData = {}, dataFromUp = {} } = useGetQueryParamsForCreditInit()
  const { pathname } = useLocation()

  const trackerInstance = useMemo(() => {
    if (!writeKey) {
      return
    }

    return createTypedTracker({
      writeKey,
      datadogActionCreator: new DatadogActionCreator(rum),
    })
  }, [])

  useEffect(() => {
    if (writeKey && trackerInstance) {
      if (applicantData?.aid) {
        trackerInstance.setAnonymousId(applicantData?.aid)
      }
    }
  }, [applicantData, trackerInstance])

  useEffect(() => {
    if (writeKey && trackerInstance) {
      if (applicantData) {
        trackerInstance.identify({
          email: applicantData?.email || dataFromUp?.email || null,
          first_name: applicantData?.firstName || null,
          last_name: applicantData?.lastName || null,
          signup_source: dataFromUp?.ltLandingPage || null,
          _bdc_org_id: bdcOrgId || undefined,
          _divvy_org_uuid: companyDivvyUuid || undefined,
        })
      } else if (email || creditApplication || dataFromUp?.email) {
        const getUserInfo = () => {
          if (creditApplication && email) {
            if (email === authorizedSigner?.email) {
              return {
                userRole: `authorizedSigner - ${authorizedSigner?.title}`,
                firstName: authorizedSigner?.firstName,
                lastName: authorizedSigner?.lastName,
              }
            }
            if (email === creditApplication?.admin?.email) {
              return {
                userRole: 'admin',
                firstName: creditApplication?.admin?.firstName,
                lastName: creditApplication?.admin?.lastName,
              }
            }
            if (creditApplication?.beneficialOwners?.find((owner: any) => email === owner?.email)) {
              const owner = creditApplication?.beneficialOwners?.find((owner: any) => email === owner?.email)
              return {
                userRole: 'companyOwner',
                firstName: owner?.firstName,
                lastName: owner?.lastName,
              }
            }
            if (email === creditApplication?.outsourcedAccountant?.email) {
              return {
                userRole: 'outsourcedAccountant',
                firstName: creditApplication?.outsourcedAccountant?.firstName,
                lastName: creditApplication?.outsourcedAccountant?.lastName,
              }
            }
            return { userRole: 'collaborator' }
          }
          return null
        }

        trackerInstance.identify({
          email: email || dataFromUp?.email || null,
          first_name: getUserInfo()?.firstName || undefined,
          last_name: getUserInfo()?.lastName || undefined,
          business_type:
            businessInfo?.entityType && businessInfo?.industry
              ? `${businessInfo?.entityType} - ${businessInfo?.industry}`
              : undefined,
          signup_source: dataFromUp?.ltLandingPage || null,
          user_role: getUserInfo()?.userRole || undefined,
          address_country: businessInfo?.physicalAddress?.countryCode || undefined,
          address_zipcode: businessInfo?.physicalAddress?.postalCode || undefined,
          _bdc_org_id: bdcOrgId || undefined,
          _divvy_org_uuid: companyDivvyUuid || undefined,
        })
      }
    }
  }, [
    applicantData,
    trackerInstance,
    email,
    authorizedSigner,
    businessInfo?.entityType,
    businessInfo?.industry,
    businessInfo?.physicalAddress?.countryCode,
    businessInfo?.physicalAddress?.postalCode,
    bdcOrgId,
    companyDivvyUuid,
    creditApplication,
    dataFromUp?.email,
    dataFromUp?.ltLandingPage,
    salesforceCreditId,
  ])

  useEffect(() => {
    if (writeKey && trackerInstance && (bdcOrgId || companyDivvyUuid)) {
      trackerInstance.group(companyDivvyUuid || '', {
        accounting_software:
          applicantData?.accountingSoftware || businessInfo?.accountingSoftware?.accountingSoftwareKey,
        _bdc_org_id: bdcOrgId || undefined,
        _divvy_org_uuid: companyDivvyUuid || undefined,
      })
    }
  }, [bdcOrgId, companyDivvyUuid, businessInfo?.accountingSoftware, trackerInstance, applicantData])

  useEffect(() => {
    if (writeKey && trackerInstance) {
      trackerInstance.init({
        _app_host_name: 'sputnik',
        _app_host_version: window?.__DivvyEnvironment?.DEPLOY_VERSION,
        _project_id: GITLAB_PROJECT_ID,
        _bdc_org_id: bdcOrgId || undefined,
        _divvy_org_uuid: companyDivvyUuid || undefined,
      })
    }
  }, [trackerInstance, bdcOrgId, companyDivvyUuid])

  // PAGE call
  useEffect(() => {
    if (writeKey && trackerInstance) {
      const pathSegments = pathname.split('/').filter(Boolean)
      const pageName = pathSegments[pathSegments.length - 1]
      if (pageName) {
        trackerInstance.page('page_load', pageName)
      }
    }
  }, [pathname, trackerInstance])

  return <WebTrackingContext value={{ tracker: trackerInstance }}>{children}</WebTrackingContext>
}
